import { useEffect, useState, useCallback } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore, UserCompanyRole } from '@stores/authStore'
import LanguageSwitcher from '@components/ui/LanguageSwitcher'
import HelpSidebar from '@components/ui/HelpSidebar'
import KycIntroModal from '@components/compliance/KycIntroModal'
import complianceApi, {
  clearLexKollerDismissed,
  consumeKycReturnPending,
  dismissKycIntro,
  dismissLexKoller,
  isComplianceDialogShown,
  isKycIntroDismissed,
  isLexKollerDismissed,
  markComplianceDialogShown,
} from '@/services/compliance'
import type { KycStep } from '@components/compliance/KycIntroModal'
import { COMPLIANCE_DIALOG_OPEN_EVENT, KYC_PROMPT_ROLES, KYC_STATUS } from '@/constants/kyc'
import type { KycStatusResponse } from '@/types/kyc'
import NotificationBell from '@components/ui/NotificationBell'
import WhatsNewModal from '@components/ui/WhatsNewModal'
import SidebarV2 from '@components/layout/SidebarV2'
import { useWhatsNew } from '@/hooks/useWhatsNew'
import { useCompanySwitch } from '@/hooks/useCompanySwitch'
import {
  Building2,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  User,
  HelpCircle,
  RefreshCw,
  Sparkles,
  BadgeCheck,
  ShieldCheck,
  Loader2,
} from 'lucide-react'
import { useVersionCheck } from '@/hooks/useVersionCheck'

/**
 * Roles the internal changelog («Neuigkeiten») is written for (STORY-1090).
 *
 * Same rule as `isInternalRole` in the auth store. External users
 * (`investor`/`signer`) never see the entries — they are development notes,
 * not product news.
 */
const INTERNAL_ROLES = new Set(['admin', 'tenant_admin', 'sales'])

export default function DashboardLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    user,
    logout,
    companies,
    activeCompanies,
  } = useAuthStore()
  const { switchCompany, pendingCompanyId } = useCompanySwitch()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [showKyc, setShowKyc] = useState(false)
  const [kycStep, setKycStep] = useState<KycStep>('intro')
  const [currentKycStep, setCurrentKycStep] = useState<KycStep>('intro')
  const [kycStatus, setKycStatus] = useState<KycStatusResponse | null>(null)
  const [kycChecked, setKycChecked] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { updateAvailable, apiVersion, reload: reloadForUpdate } = useVersionCheck()
  const { hasNew: hasWhatsNew, markSeen: markWhatsNewSeen } = useWhatsNew()
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)

  // Add subtle shadow when page is scrolled past the top
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // STORY-1090: The changelog is an internal developer log — buyers/sellers
  // neither get the button nor the dialog.
  const showChangelog = !!user && INTERNAL_ROLES.has(user.role)

  // Auto-open changelog when new version detected.
  // Never on top of the compliance dialog — one dialog at a time.
  useEffect(() => {
    if (showChangelog && hasWhatsNew && !showKyc) {
      const timer = setTimeout(() => {
        setWhatsNewOpen(true)
        markWhatsNewSeen()
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [hasWhatsNew, showChangelog, showKyc]) // eslint-disable-line react-hooks/exhaustive-deps

  // Impersonation: check if admin session is stored
  const [isImpersonating, setIsImpersonating] = useState(() => !!sessionStorage.getItem('impersonation-return'))

  const endImpersonation = useCallback(() => {
    const stored = sessionStorage.getItem('impersonation-return')
    if (!stored) return
    try {
      const adminAuth = JSON.parse(stored)
      useAuthStore.getState().setAuth(
        adminAuth.user,
        adminAuth.accessToken,
        adminAuth.refreshToken,
        adminAuth.companies,
        adminAuth.contracts,
      )
      sessionStorage.removeItem('impersonation-return')
      setIsImpersonating(false)
      window.location.href = '/admin/settings/mandanten'
    } catch {
      sessionStorage.removeItem('impersonation-return')
      setIsImpersonating(false)
    }
  }, [])

  // GwG identification status for external users (STORY-990).
  // Internal staff is never prompted, so the call is skipped for them.
  useEffect(() => {
    if (!user || !KYC_PROMPT_ROLES.includes(user.role)) {
      setKycStatus(null)
      setKycChecked(true)
      return
    }
    let cancelled = false
    setKycChecked(false)
    complianceApi
      .getKycStatus()
      .then((status) => {
        if (!cancelled) setKycStatus(status)
      })
      .catch(() => {
        // Endpoint unavailable — no dialog, the rest of the app stays usable
      })
      .finally(() => {
        if (!cancelled) setKycChecked(true)
      })
    return () => { cancelled = true }
  }, [user?.id, user?.role]) // eslint-disable-line react-hooks/exhaustive-deps

  const kycRequired = !!kycStatus?.required

  /** Identification itself still open — `required` and not yet verified. */
  const identificationOpen = kycRequired && kycStatus?.contact?.status !== KYC_STATUS.VERIFIED

  // Back from the ident provider (STORY-991): show the check run right away,
  // no matter whether an identification is still "required".
  const [kycReturned, setKycReturned] = useState(false)
  useEffect(() => {
    if (!user || !consumeKycReturnPending(user.id)) return
    // Counts as the compliance dialog of this session — see the auto-open below
    markComplianceDialogShown(user.id)
    setKycStep('progress')
    setShowKyc(true)
    setKycReturned(true)
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Lex Koller still open (STORY-989/1000).
   *
   * Not a second dialog any more — the assessment is the second step of the
   * identification dialog. After closing/skipping it stays closed; the
   * assessment remains startable in the settings.
   */
  const lexKollerRequired =
    !!user && user.role === 'investor' && !user.lex_koller_status && !isLexKollerDismissed(user.id)

  // Show the compliance dialog once per session while something is still open.
  // «Später» is allowed — it returns on the next login.
  useEffect(() => {
    if (!user || !kycChecked || kycReturned || showKyc) return
    // Exactly one dialog per session (STORY-1000). Without this the dialog
    // would reopen 1.5 s after closing: a completed identification clears
    // `kycRequired`, which turns the still open assessment into a fresh
    // «lexKollerOnly» reason to open — the second dialog the issue forbids.
    if (isComplianceDialogShown(user.id)) return
    const identOpen = kycRequired && !isKycIntroDismissed(user.id)
    // The assessment alone only opens the dialog when there is nothing left to
    // identify — whoever postponed the identification is not asked again today.
    const lexKollerOnly = !kycRequired && lexKollerRequired
    if (!identOpen && !lexKollerOnly) return
    const timer = setTimeout(() => {
      // Delay slightly so the user sees the dashboard first
      markComplianceDialogShown(user.id)
      setKycStep(identOpen ? 'intro' : 'lex_koller')
      setShowKyc(true)
    }, 1500)
    return () => clearTimeout(timer)
  }, [user?.id, kycChecked, kycRequired, kycReturned, lexKollerRequired, showKyc]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Open the dialog on request (STORY-1161).
   *
   * The dashboard hint is the single call to action of this onboarding step; it
   * asks for the dialog through a window event because it renders inside the
   * `<Outlet />` of this layout. A dismissed dialog must open again here — the
   * user just asked for it.
   */
  useEffect(() => {
    const openOnRequest = () => {
      setKycStep(identificationOpen ? 'intro' : 'lex_koller')
      setShowKyc(true)
    }
    window.addEventListener(COMPLIANCE_DIALOG_OPEN_EVENT, openOnRequest)
    return () => window.removeEventListener(COMPLIANCE_DIALOG_OPEN_EVENT, openOnRequest)
  }, [identificationOpen])

  const handleKycClose = useCallback(() => {
    setShowKyc(false)
    setKycReturned(false)
    if (!user) return
    dismissKycIntro(user.id)
    // Only a dialog that actually reached the assessment counts as skipped —
    // whoever stops at the identification sees Lex Koller again next time.
    if (currentKycStep === 'lex_koller' && !user.lex_koller_status) {
      dismissLexKoller(user.id)
    }
  }, [user, currentKycStep])

  const handleLexKollerComplete = useCallback((status: string) => {
    if (!user) return
    clearLexKollerDismissed(user.id)
    useAuthStore.getState().setUser({ ...user, lex_koller_status: status })
  }, [user])


  // STORY-110: Single-select, always exactly 1 company active (no "Alle Firmen")
  // STORY-1033: Persist the Mandant server-side; the menu stays open on failure
  const handleCompanyClick = async (company: UserCompanyRole) => {
    if (await switchCompany(company)) setCompanyMenuOpen(false)
  }

  const isCompanySelected = (company: UserCompanyRole) =>
    activeCompanies.some((c) => c.company_id === company.company_id)

  const getCompanyButtonLabel = (): string => {
    if (activeCompanies.length === 0) return t('company.noSelection')
    return activeCompanies[0].company_name
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SidebarV2 */}
      <SidebarV2 isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content — bewusst KEIN overflow-x-hidden hier: würde zu impliziter
          overflow-y:auto Coercion führen und position:sticky im <header> brechen.
          Horizontaler Overflow ist global via html/body in src/index.css abgesichert. */}
      <div className="lg:ml-64">
        {/* Top Header — sticky, mit dezentem Schatten beim Scrollen */}
        <header className={`sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 transition-shadow ${scrolled ? 'shadow-md' : ''}`}>
          {/* Mobile Menu Button */}
          <button
            data-testid="dashboard-layout-sidebar-open-btn"
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* STORY-014b: Company Multi-Select Switcher */}
            {companies.length > 1 && (
              <div className="relative">
                <button
                  data-testid="dashboard-layout-company-menu-open-btn"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                  onClick={() => setCompanyMenuOpen(!companyMenuOpen)}
                >
                  <Building2 size={18} className="text-primary-600" />
                  <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[150px] truncate">
                    {getCompanyButtonLabel()}
                  </span>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>

                {/* Company Dropdown */}
                {companyMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[55]"
                      onClick={() => setCompanyMenuOpen(false)}
                    />
                    <div className="fixed left-4 right-4 top-[4.5rem] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[60]">
                      {/* Header */}
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-xs font-medium text-gray-500 uppercase">
                          {t('company.switch')}
                        </div>
                      </div>

                      {/* STORY-110: Company list (single-select, no "Alle Firmen") */}
                      <div className="max-h-64 overflow-y-auto">
                        {companies.map((company) => {
                          const selected = isCompanySelected(company)
                          const pending = pendingCompanyId === company.company_id
                          return (
                          <button
                            data-testid="dashboard-layout-company-click-btn"
                            key={company.id}
                            role="menuitemradio"
                            aria-checked={selected}
                            aria-busy={pending}
                            disabled={pendingCompanyId !== null}
                            className={`flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors disabled:cursor-not-allowed ${
                              selected ? 'bg-primary-50' : 'hover:bg-gray-50'
                            }`}
                            onClick={(e) => { e.stopPropagation(); void handleCompanyClick(company) }}
                          >
                            <div className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${
                              selected
                                ? 'border-primary-600'
                                : 'border-gray-300'
                            }`}>
                              {selected && (
                                <div className="w-2 h-2 rounded-full bg-primary-600" />
                              )}
                            </div>
                            <Building2 size={16} className="text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {company.company_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {company.function || company.company_uid}
                              </div>
                            </div>
                            {pending && (
                              <Loader2
                                size={16}
                                className="animate-spin text-gray-400 flex-shrink-0"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* GwG identification status (STORY-990).
                No «Identifikation ausstehend» button any more (STORY-1161): the
                open step is announced once, by the hint on the dashboard. */}
            {kycChecked && kycStatus && (
              kycStatus.contact?.status === KYC_STATUS.VERIFIED ? (
                /* Mobile: icon only, from sm: with label (STORY-1126) */
                <span
                  data-testid="dashboard-layout-kyc-badge-verified"
                  role="img"
                  aria-label={t('kyc.badge.verified', 'Identifiziert')}
                  title={t('kyc.badge.verified', 'Identifiziert')}
                  className="inline-flex items-center gap-1 px-1.5 sm:px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200"
                >
                  <BadgeCheck size={14} />
                  <span className="hidden sm:inline">{t('kyc.badge.verified', 'Identifiziert')}</span>
                </span>
              ) : !kycStatus.required ? (
                /* Exempt (e.g. supervised company) — never «Identifiziert» (STORY-1113) */
                <span
                  data-testid="dashboard-layout-kyc-badge-exempt"
                  role="img"
                  aria-label={t('kyc.badge.exempt', 'Keine Identifikation nötig')}
                  title={t('kyc.badge.exemptHint', 'Ihre Firma ist beaufsichtigt (FINMA o. ä.) — eine persönliche Identifikation ist nicht nötig.')}
                  className="inline-flex items-center gap-1 px-1.5 sm:px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200"
                >
                  <ShieldCheck size={14} aria-hidden="true" />
                  <span className="hidden sm:inline">{t('kyc.badge.exempt', 'Keine Identifikation nötig')}</span>
                </span>
              ) : null
            )}

            {/* Help Button */}
            <button
              data-testid="dashboard-layout-help-open-btn"
              onClick={() => setHelpOpen(true)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={t('help.title')}
            >
              <HelpCircle size={20} />
            </button>

            {/* What's New — internal roles only (STORY-1090) */}
            {showChangelog && (
              <button
                data-testid="dashboard-layout-whats-new-open-btn"
                onClick={() => { setWhatsNewOpen(true); markWhatsNewSeen() }}
                className="relative p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                title={t('changelog.title', 'Neuigkeiten')}
                aria-label={t('changelog.title', 'Neuigkeiten')}
              >
                <Sparkles size={20} />
                {hasWhatsNew && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full ring-2 ring-white" />
                )}
              </button>
            )}

            {/* Notification Bell */}
            <NotificationBell />

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* User Menu */}
            <div className="relative">
              <button
                data-testid="dashboard-layout-user-menu-open-btn"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <User size={18} className="text-primary-600" />
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.first_name || user?.email}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t(`roles.${user?.role}`)}
                  </div>
                </div>
                <ChevronDown size={16} className="text-gray-400" />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[55]"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[60]">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                    <NavLink
                      data-testid="dashboard-layout-user-menu-open-link"
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings size={16} />
                      {t('auth.settings')}
                    </NavLink>
                    <hr className="my-1" />
                    <button
                      data-testid="dashboard-layout-logout-btn"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                      onClick={handleLogout}
                    >
                      <LogOut size={16} />
                      {t('auth.logout')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        {/* Update available banner */}
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm">
            <LogOut size={14} />
            <span>
              {t('layout.impersonating', {
                name: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email,
                defaultValue: 'Eingeloggt als {{name}}',
              })}
            </span>
            <button
              data-testid="dashboard-layout-end-impersonation-btn"
              onClick={endImpersonation}
              className="underline font-medium hover:text-white/80"
            >
              {t('layout.endImpersonation', 'Zurück zum Admin')}
            </button>
          </div>
        )}

        {updateAvailable && (
          <div className="bg-primary-600 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm">
            <RefreshCw size={14} className="animate-spin-slow" />
            <span>{t('layout.updateAvailable')}</span>
            <button
              data-testid="dashboard-layout-reload-for-update-btn"
              onClick={reloadForUpdate}
              className="underline font-medium hover:text-white/80"
            >
              {t('layout.reloadNow')}
            </button>
          </div>
        )}

        <main className="p-4 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="py-3 px-4 lg:px-8 border-t border-gray-100 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Immotron · Uniplacement GmbH. {t('layout.footer.allRightsReserved')} · Web v{__APP_VERSION__}{__BUILD_NUMBER__ !== 'dev' ? `+${__BUILD_NUMBER__}` : ''}{apiVersion ? ` · API v${apiVersion}` : ''} · {t('layout.footer.build')} {__BUILD_DATE__}
        </footer>
      </div>

      {/* Help Sidebar */}
      <HelpSidebar isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Compliance dialog: identification + Lex Koller in one (STORY-1000) */}
      <KycIntroModal
        isOpen={showKyc}
        status={kycStatus}
        initialStep={kycStep}
        onClose={handleKycClose}
        onSkip={handleKycClose}
        onStatusChange={setKycStatus}
        lexKollerRequired={lexKollerRequired}
        onLexKollerComplete={handleLexKollerComplete}
        onStepChange={setCurrentKycStep}
      />

      {/* What's New Modal — internal roles only (STORY-1090) */}
      {showChangelog && (
        <WhatsNewModal open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
      )}
    </div>
  )
}
